import { useEffect, useState } from "react";
import { api, imgSrc } from "@/lib/api";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { COLORING_CATEGORIES } from "@/data/static";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function ColoringGallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("ALL");
  const [zoom, setZoom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery", { params: { type: "coloring" } })
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = active === "ALL" ? items : items.filter((i) => i.category === active);

  return (
    <div data-testid="coloring-gallery-page" className="pt-32 pb-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine delay={0.1}><p className="overline-label">Coloring Gallery</p></MaskedLine>
        <h1 className="mt-4 font-cinzel font-black text-brand-burgundy text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          <MaskedLine delay={0.25}>THE COLOR</MaskedLine>
          <MaskedLine delay={0.4} innerClassName="text-gold-gradient">ARCHIVE.</MaskedLine>
        </h1>
        <MaskedLine delay={0.55} className="mt-6">
          <p className="max-w-2xl text-base leading-relaxed text-brand-ink-muted">
            Setiap warna adalah karya seni. Jelajahi arsip pewarnaan kami per kategori — dari warna basic, ash, highlight, balayage, hingga warna kekinian. Wujudkan warna impianmu.
          </p>
        </MaskedLine>

        <div className="mt-12 flex flex-wrap gap-2.5" data-testid="coloring-category-tabs">
          {COLORING_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`coloring-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setActive(c)}
              className={`rounded-full px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                active === c
                  ? "bg-brand-burgundy text-brand-cream shadow-[0_8px_25px_rgba(74,14,23,0.3)]"
                  : "bg-white text-brand-ink-muted gold-foil-border hover:text-brand-burgundy hover:border-brand-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-20 text-center text-brand-ink-muted" data-testid="coloring-loading">Memuat galeri...</p>
        ) : filtered.length === 0 ? (
          <div className="mt-20 rounded-sm gold-foil-border bg-brand-paper p-16 text-center" data-testid="coloring-empty">
            <p className="font-serif text-xl text-brand-burgundy">Belum ada foto di kategori ini</p>
            <p className="mt-2 text-sm text-brand-ink-muted">Foto akan segera diunggah oleh tim kami. Nantikan!</p>
          </div>
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="coloring-grid">
            {filtered.map((item, i) => (
              <Reveal key={item.id} delay={(i % 3) * 0.08}>
                <button
                  data-testid={`coloring-item-${item.id}`}
                  onClick={() => setZoom(item)}
                  className="group block w-full text-left"
                >
                    <div className="relative aspect-[4/5] overflow-hidden rounded-sm gold-foil-border">
                      <img
                        src={imgSrc(item.image)}
                        alt={item.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      <span className="absolute left-4 top-4 rounded-full bg-brand-cream/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-burgundy backdrop-blur-sm">
                        {item.category}
                      </span>
                    </div>
                  </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent className="max-w-3xl bg-brand-cream gold-foil-border p-2" data-testid="coloring-lightbox">
          {zoom && (
            <div>
              <img src={imgSrc(zoom.image)} alt={zoom.title} className="max-h-[70vh] w-full rounded-sm object-cover" />
              <div className="p-5">
                <p className="overline-label !text-[10px]">{zoom.category}</p>
                <h3 className="mt-1 font-serif text-xl font-semibold text-brand-burgundy">{zoom.title}</h3>
                {zoom.description && <p className="mt-1 text-sm text-brand-ink-muted">{zoom.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
